import { gql } from '@apollo/client';
import { useDisclosure } from '@dwarvesf/react-hooks';
import {
  Box,
  Checkbox,
  IconButton,
  Input,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { IconBoldCloseCircle } from 'components/icons/components/bold/IconBoldCloseCircle';
import { IconCustomSmiley } from 'components/icons/components/custom/IconCustomSmiley';
import { IconCustomSort } from 'components/icons/components/custom/IconCustomSort';
import { IconCustomSortAsc } from 'components/icons/components/custom/IconCustomSortAsc';
import { IconCustomSortDesc } from 'components/icons/components/custom/IconCustomSortDesc';
import { IconCustomTodo } from 'components/icons/components/custom/IconCustomTodo';
import { IconLinearMessage } from 'components/icons/components/linear/IconLinearMessage';
import { IconLinearShoppingCart } from 'components/icons/components/linear/IconLinearShoppingCart';
import { IconLinearTag2 } from 'components/icons/components/linear/IconLinearTag2';
import { IconOutlineSearchNormal1 } from 'components/icons/components/outline/IconOutlineSearchNormal1';
import { NestedFiltersMenuView } from 'features/nestedFilters/views/menu';
import {
  RelationshipBackgroundColorMap,
  RelationshipColorMap,
  RelationshipLabelMap,
  SentimentLabelMap,
} from 'features/socialListeningCommunity/constants';
import {
  CreatorProfileCommunitiesFilters,
  CreatorProfileCommunitiesSortBy,
  CreatorProfileCommunitiesSortByInput,
  CreatorProfileRelationshipStrength,
  Sentiment,
  SortOrder,
  useGetCapturedProductsForSlCommunityTableLazyQuery,
  useGetCommunitiesForEmergingCommunityTableQuery,
  useTopCapturedProductsForEmergingCommunityQuery,
} from 'graphql/generated';
import { debounce } from 'lodash';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import { useNavigate } from 'react-router-dom';
import { PlotRoutes } from 'Routes';
import { theme } from 'styles/theme';
import { formatBigNumber } from 'utils/number';
import { COMMUNITY_MODAL_TYPES } from './SocialListeningCommunityDialog';

// eslint-disable-next-line
gql`
  query GetCommunitiesForEmergingCommunityTable(
    $input: GetCreatorProfileCommunitiesArgs!
  ) {
    getCreatorProfileCommunities(input: $input) {
      pageInfo {
        hasNextPage
      }
      data {
        community
        mentionCount
        relationshipStrength
        totalEngagement
        totalViews
      }
    }
  }
`;

// eslint-disable-next-line
gql`
  query GetCapturedProductsForSLCommunityTable(
    $input: GetCreatorProfilesCapturedProductsArgs!
  ) {
    getCreatorProfilesCapturedProducts(input: $input) {
      data {
        id
        line
        model
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

// eslint-disable-next-line
gql`
  query TopCapturedProductsForEmergingCommunity(
    $input: TopCapturedProductsForEmergingCommunityInput!
  ) {
    topCapturedProductsForEmergingCommunity(input: $input) {
      id
      line
      model
    }
  }
`;

interface SocialListeningEmergingCommunityTableProps
  extends Pick<
    CreatorProfileCommunitiesFilters,
    'brandId' | 'dateRange' | 'platforms'
  > {
  selectedCommunities: string[];
  setSelectedCommunities: Dispatch<SetStateAction<string[]>>;
}

export const SocialListeningEmergingCommunityTable = ({
  brandId,
  dateRange,
  platforms,
  selectedCommunities,
  setSelectedCommunities,
}: SocialListeningEmergingCommunityTableProps) => {
  const navigate = useNavigate();

  const [selectedTableSort, setSelectedTableSort] =
    useState<CreatorProfileCommunitiesSortByInput>({
      field: CreatorProfileCommunitiesSortBy.MentionCount,
      order: SortOrder.Desc,
    });
  const [searchedTerm, setSearchedTerm] = useState('');
  const [filterValues, setFilterValues] = useState<Record<string, any>>({});
  const searchExpand = useDisclosure();
  const debouncedSearchTerm = debounce((value) => setSearchedTerm(value), 300);

  const [getCapturedProducts] =
    useGetCapturedProductsForSlCommunityTableLazyQuery();

  const communityFilters: CreatorProfileCommunitiesFilters = useMemo(
    () => ({
      brandId,
      dateRange,
      platforms,
      query: searchedTerm,
      relationshipsStrength:
        filterValues.relationshipsGroup?.relationships?.value.map(
          (v) => v.value as CreatorProfileRelationshipStrength,
        ),
      sentiments: filterValues.sentimentsGroup?.sentiments?.value.map(
        (v) => v.value as Sentiment,
      ),
      ...((filterValues.mentionsGroup?.mentions?.value.min ||
        filterValues.mentionsGroup?.mentions?.value.max) && {
        brandMentionsCount: {
          ...(filterValues.mentionsGroup?.mentions?.value.max && {
            max: Number(filterValues.mentionsGroup?.mentions?.value.max),
          }),
          ...(filterValues.mentionsGroup?.mentions?.value.min && {
            min: Number(filterValues.mentionsGroup?.mentions?.value.min),
          }),
        },
      }),
      ...((filterValues.commentsGroup?.comments?.value.min ||
        filterValues.commentsGroup?.comments?.value.max) && {
        commentsCount: {
          ...(filterValues.commentsGroup?.comments?.value.max && {
            max: Number(filterValues.commentsGroup?.comments?.value.max),
          }),
          ...(filterValues.commentsGroup?.comments?.value.min && {
            min: Number(filterValues.commentsGroup?.comments?.value.min),
          }),
        },
      }),
      ...(selectedTableSort && { sortBy: selectedTableSort }),
      ...(filterValues.capturedProductsGroup?.capturedProducts?.value
        .length && {
        capturedProductIds:
          filterValues.capturedProductsGroup?.capturedProducts?.value.map(
            (v) => v.value,
          ),
      }),
    }),
    [
      brandId,
      dateRange,
      platforms,
      searchedTerm,
      selectedTableSort,
      filterValues,
    ],
  );

  const {
    data: communitiesData,
    fetchMore: fetchMoreCommunities,
    loading: communitiesDataLoading,
  } = useGetCommunitiesForEmergingCommunityTableQuery({
    variables: {
      input: {
        filters: communityFilters,
      },
    },
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      if (!selectedCommunities.length) {
        setSelectedCommunities(
          data.getCreatorProfileCommunities.data
            .slice(0, 5)
            .map((c) => c.community),
        );
      }
    },
  });

  const communities = useMemo(() => {
    return communitiesData?.getCreatorProfileCommunities.data ?? [];
  }, [communitiesData]);

  const headers = useMemo(() => {
    return [
      { label: 'Communities', key: 'community', minWidth: 400 },
      {
        label: 'Total Mentions',
        key: CreatorProfileCommunitiesSortBy.MentionCount,
        canSort: true,
      },
      {
        label: 'Total Engagement',
        key: CreatorProfileCommunitiesSortBy.TotalEngagement,
        canSort: true,
      },
      {
        label: 'Total View Count',
        key: CreatorProfileCommunitiesSortBy.TotalViews,
        canSort: true,
      },
      {
        label: 'Overall Relationship',
        key: CreatorProfileCommunitiesSortBy.RelationshipStrength,
        canSort: true,
      },
      { label: 'Top Products', key: 'capturedProducts', minWidth: 250 },
    ];
  }, []);
  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 3,
        }}
      >
        {searchExpand.isOpen ? (
          <Input
            disableUnderline
            fullWidth
            placeholder="Search for a brand, category, attribute or product line"
            autoFocus
            onChange={(e) => {
              debouncedSearchTerm(e.target.value);
            }}
            startAdornment={
              <IconOutlineSearchNormal1
                size={16}
                style={{
                  marginRight: theme.spacing(2),
                  color: theme.colors?.utility[700],
                }}
              />
            }
            endAdornment={
              <IconBoldCloseCircle
                size={20}
                style={{
                  color: theme.colors?.utility[700],
                  cursor: 'pointer',
                }}
                onClick={() => {
                  searchExpand.onClose();
                  setSearchedTerm('');
                }}
              />
            }
            sx={{
              borderRadius: 25,
              background: theme.colors?.primary.white,
              padding: theme.spacing(1, 4),
              ...theme.typography['subhead-xl'],
            }}
          />
        ) : (
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <IconButton
              sx={{
                borderRadius: 25,
                background: theme.colors?.primary.white,
                width: 40,
                height: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 0,
              }}
              onClick={searchExpand.onOpen}
            >
              <IconOutlineSearchNormal1 size={16} />
            </IconButton>
          </Box>
        )}
        <NestedFiltersMenuView
          values={filterValues}
          onChange={(values) => {
            setFilterValues(values);
          }}
          componentsProps={{
            trigger: {
              sx: {
                background: theme.colors?.primary.white,
              },
            },
          }}
          items={[
            {
              type: 'group',
              key: 'relationshipsGroup',
              label: 'Relationship',
              Icon: IconCustomTodo,
              items: [
                {
                  type: 'multi-select',
                  key: 'relationships',
                  Icon: IconCustomTodo,
                  options: [
                    CreatorProfileRelationshipStrength.VeryStrong,
                    CreatorProfileRelationshipStrength.Strong,
                    CreatorProfileRelationshipStrength.Neutral,
                    CreatorProfileRelationshipStrength.Weak,
                    CreatorProfileRelationshipStrength.VeryWeak,
                  ].map((value) => ({
                    label: RelationshipLabelMap[value],
                    value,
                  })),
                },
              ],
            },
            {
              type: 'group',
              key: 'sentimentsGroup',
              label: 'Sentiment',
              Icon: IconCustomSmiley,
              items: [
                {
                  type: 'multi-select',
                  key: 'sentiments',
                  Icon: IconCustomSmiley,
                  options: [
                    Sentiment.Positive,
                    Sentiment.Neutral,
                    Sentiment.Negative,
                  ].map((value) => ({
                    label: SentimentLabelMap[value],
                    value,
                  })),
                },
              ],
            },
            {
              type: 'group',
              key: 'capturedProductsGroup',
              label: 'Products',
              Icon: IconLinearShoppingCart,
              items: [
                {
                  type: 'multi-select',
                  key: 'capturedProducts',
                  options: [],
                  Icon: IconLinearShoppingCart,
                  filterable: true,
                  getAsyncOptions: async ({ query, nextPageCursor }) => {
                    const capturedProductsData = await getCapturedProducts({
                      variables: {
                        input: {
                          filters: { brandId, query },
                          after: nextPageCursor,
                        },
                      },
                      fetchPolicy: 'network-only',
                    });
                    const capturedProducts =
                      capturedProductsData?.data
                        ?.getCreatorProfilesCapturedProducts.data || [];
                    const endCursor = capturedProductsData?.data
                      ?.getCreatorProfilesCapturedProducts.pageInfo?.hasNextPage
                      ? capturedProductsData?.data
                          ?.getCreatorProfilesCapturedProducts.pageInfo
                          ?.endCursor ?? ''
                      : undefined;

                    return {
                      options: capturedProducts
                        .filter(
                          (capturedProduct) =>
                            capturedProduct.model || capturedProduct.line,
                        )
                        .map((capturedProduct) => ({
                          label: capturedProduct.model || capturedProduct.line,
                          value: capturedProduct.id,
                        })),
                      nextPageCursor: endCursor,
                    };
                  },
                },
              ],
            },
            {
              type: 'group',
              key: 'mentionsGroup',
              label: 'Mentions',
              Icon: IconLinearTag2,
              items: [
                {
                  type: 'form',
                  key: 'mentions',
                  Icon: IconLinearTag2,
                  fields: [
                    {
                      type: 'number',
                      key: 'min',
                      label: 'Minimum number of mentions',
                    },
                    {
                      type: 'number',
                      key: 'max',
                      label: 'Maximum number of mentions',
                      rules: {
                        max: {
                          value: 999999,
                          message: 'Maximum value exceeded',
                        },
                        min: {
                          value: 0,
                          message: 'Minimum value is 0',
                        },
                      },
                    },
                  ],
                },
              ],
            },
            {
              type: 'group',
              key: 'commentsGroup',
              label: 'Comments',
              Icon: IconLinearMessage,
              items: [
                {
                  type: 'form',
                  key: 'comments',
                  Icon: IconLinearMessage,
                  fields: [
                    {
                      type: 'number',
                      key: 'min',
                      label: 'Minimum number of comments',
                    },
                    {
                      type: 'number',
                      key: 'max',
                      label: 'Maximum number of comments',
                      rules: {
                        max: {
                          value: 999999,
                          message: 'Maximum value exceeded',
                        },
                        min: {
                          value: 0,
                          message: 'Minimum value is 0',
                        },
                      },
                    },
                  ],
                },
              ],
            },
          ]}
        />
      </Box>
      <TableContainer sx={{ maxHeight: 320, overflowY: 'auto' }}>
        <InfiniteScroll
          loadMore={() => {
            fetchMoreCommunities({
              variables: {
                input: {
                  filters: communityFilters,
                  skip: communitiesData?.getCreatorProfileCommunities.data
                    .length,
                },
              },
              updateQuery: (prev, { fetchMoreResult }) => {
                if (!fetchMoreResult) return prev;
                return {
                  ...fetchMoreResult,
                  getCreatorProfileCommunities: {
                    ...fetchMoreResult.getCreatorProfileCommunities,
                    data: [
                      ...prev.getCreatorProfileCommunities.data,
                      ...fetchMoreResult.getCreatorProfileCommunities.data,
                    ],
                  },
                };
              },
            });
          }}
          threshold={510}
          useWindow={false}
          initialLoad={false}
          hasMore={
            communitiesData?.getCreatorProfileCommunities.pageInfo.hasNextPage
          }
        >
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  th: {
                    padding: 2.5,
                  },
                }}
              >
                <TableCell sx={{ width: 20 }}>
                  <Checkbox
                    size="small"
                    sx={{
                      p: 0,
                    }}
                    checked={selectedCommunities.length !== 0}
                    onClick={() => {
                      if (selectedCommunities.length === 0) {
                        setSelectedCommunities(
                          communitiesData?.getCreatorProfileCommunities.data.map(
                            (c) => c.community,
                          ) || [],
                        );
                      } else {
                        setSelectedCommunities([]);
                      }
                    }}
                  />
                </TableCell>
                {headers.map((header) => (
                  <TableCell
                    key={header.key}
                    sx={{
                      minWidth: header.minWidth ?? 150,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography
                        variant="headline-xs"
                        color={theme.colors?.utility[700]}
                      >
                        {header.label}
                      </Typography>
                      {header.canSort &&
                        !!communitiesData?.getCreatorProfileCommunities.data
                          .length && (
                          <IconButton
                            sx={{
                              p: 0,
                              color: theme.colors?.utility[600],
                              display: communities.length ? 'block' : 'none',
                            }}
                            disableRipple
                            onClick={() => {
                              setSelectedTableSort((prev) => {
                                if (prev?.field === header.key) {
                                  return {
                                    ...prev,
                                    order:
                                      prev?.order === SortOrder.Asc
                                        ? SortOrder.Desc
                                        : SortOrder.Asc,
                                  };
                                }
                                return {
                                  field: header.key,
                                  order: SortOrder.Asc,
                                };
                              });
                            }}
                          >
                            {selectedTableSort?.field === header.key &&
                            selectedTableSort?.order === SortOrder.Asc ? (
                              <IconCustomSortAsc
                                size={16}
                                color={theme.colors?.utility[800]}
                              />
                            ) : selectedTableSort?.field === header.key &&
                              selectedTableSort?.order === SortOrder.Desc ? (
                              <IconCustomSortDesc
                                size={16}
                                color={theme.colors?.utility[800]}
                              />
                            ) : (
                              <IconCustomSort
                                size={16}
                                color={theme.colors?.utility[600]}
                              />
                            )}
                          </IconButton>
                        )}
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!communities.length ? (
                <TableRow>
                  <TableCell
                    colSpan={headers.length + 1}
                    sx={{ border: 'none' }}
                  >
                    <Typography
                      variant="headline-xs"
                      color={theme.colors?.utility[600]}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {communitiesDataLoading
                        ? 'Loading...'
                        : 'No communities found'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                communities.map((row, index) => (
                  <TableRow
                    key={`${row.community}-${index}`}
                    sx={{
                      cursor: 'pointer',
                      td: {
                        p: 2.5,
                        border: 'none',
                        ...theme.typography['subhead-xl'],
                        textAlign: 'left',
                      },
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(
                        `${PlotRoutes.socialListeningCommunity(
                          brandId,
                        )}?modalType=${
                          COMMUNITY_MODAL_TYPES.CommunityPosts
                        }&community=${row.community}`,
                      );
                    }}
                  >
                    <TableCell
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <Checkbox
                        size="small"
                        sx={{
                          p: 0,
                        }}
                        checked={selectedCommunities.includes(row.community)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedCommunities([
                              ...selectedCommunities,
                              row.community,
                            ]);
                          } else {
                            setSelectedCommunities(
                              selectedCommunities.filter(
                                (c) => c !== row.community,
                              ),
                            );
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="headline-sm">
                        {row.community}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatBigNumber(row.mentionCount)}</TableCell>
                    <TableCell>
                      {formatBigNumber(row.totalEngagement)}
                    </TableCell>
                    <TableCell>{formatBigNumber(row.totalViews)}</TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          color: RelationshipColorMap[row.relationshipStrength],
                          bgcolor:
                            RelationshipBackgroundColorMap[
                              row.relationshipStrength
                            ],
                          borderRadius: 7,
                          padding: theme.spacing(1, 2),
                          ...theme.typography['headline-sm'],
                          width: 'fit-content',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {RelationshipLabelMap[row.relationshipStrength]}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <TopCapturedProductsForEmergingCommunity
                        brandId={brandId}
                        community={row.community}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </InfiniteScroll>
      </TableContainer>
    </>
  );
};

interface TopCapturedProductsForEmergingCommunityProps {
  brandId: string;
  community: string;
}

const TopCapturedProductsForEmergingCommunity = ({
  brandId,
  community,
}: TopCapturedProductsForEmergingCommunityProps) => {
  const { data } = useTopCapturedProductsForEmergingCommunityQuery({
    variables: {
      input: { brandId, community },
    },
  });

  return (
    // eslint-disable-next-line
    <>
      {data?.topCapturedProductsForEmergingCommunity?.length
        ? data.topCapturedProductsForEmergingCommunity
            .map((p) => p.line || p.model)
            .join(', ')
        : '-'}
    </>
  );
};
